import { inheritQueryString } from '@/packages/string';
import Context from '../pageContext';
import { switchCase } from '@/packages/switch';
import { ButtonVariants } from '@/ui-components/atoms/Button/Button';
import PhoneNumber from './PhoneNumber';
import urlPrefixRelative from '@/packages/string/urlPrefixRelative';
import { URL_INHERIT_PARAMS } from '@/constants';

type ThemeType = 'Primary CTA' | 'Secondary CTA' | 'White CTA' | 'Custom CTA' | 'Tertiary';
type ButtonThemesWithWeb2 = ButtonVariants;

const cases: { [key in ThemeType]: ButtonThemesWithWeb2 } = {
    ['Primary CTA']: 'primary',
    ['Secondary CTA']: 'secondary',
    ['White CTA']: 'primary',
    ['Custom CTA']: 'custom',
    ['Tertiary']: 'tertiary',
};

const themeSwitch = () => switchCase<ThemeType, ButtonThemesWithWeb2>(cases)('tertiary');

export default function Link(module: ContentfulLink, pageContext: Context): SiteLink {
    let href = module.fields.href || '';
    let media = module.fields.media;

    if (pageContext?.context?.relativeUrlDomain) {
        if (href) {
            href = urlPrefixRelative(pageContext.context.relativeUrlDomain, href);
        }

        if (media?.url && media.url.trim().charAt(0) === '/') {
            media = JSON.parse(JSON.stringify(media)) as AEMImage;
            media.url = urlPrefixRelative(pageContext.context.relativeUrlDomain, media.url);
        }
    }

    const inheritRegex = /(subscribe\/checkout)|(\/onboarding\/setup\-credentials)/gi;
    const shouldInherit = !!module.fields.inheritUrlParameters || inheritRegex.test(href);

    if (shouldInherit && typeof pageContext?.get === 'function') {
        const searchParams = pageContext.get<StringObject>('searchParams') || {};
        href = inheritQueryString(href, searchParams, URL_INHERIT_PARAMS);
    }

    let sitePhoneNumber: SiteLink | undefined;
    if (module.fields.phone_number) {
        sitePhoneNumber = PhoneNumber(module.fields.phone_number);
    }

    let ctaColor: string = 'transparent';
    if (module.fields.ctaColor) {
        ctaColor = module.fields.ctaColor[0] !== '#' ? `#${module.fields.ctaColor}` : module.fields.ctaColor;
    }

    const target = module.fields.link_target === 'New Tab' ? '_blank' : '';

    // The figma source's color name includes the "subset-" prefix:
    const colorSet =
        module.fields.cta_color_set === 'sxm-blue' ? 'subset-sxm-blue' : module.fields.cta_color_set || 'base';

    const colorSetForTheme =
        module.fields.link_type === 'White CTA'
            ? colorSet === 'subset-sxm-blue'
                ? 'subset-sxm-blue-overlay'
                : 'overlay'
            : colorSet;

    const recommendedOffer = typeof pageContext?.get === 'function' ? pageContext.get<SiteOffer>('recommended_offer') : undefined;
    const recommendedCta = recommendedOffer?.fields.cta;
    const useRecommendedCta = module.fields.offer_link && recommendedCta;

    return {
        cms_id: module.sys.id,
        cms_type: module.sys.contentType.sys.id,
        fields: {
            name: module.fields.name,
            href: useRecommendedCta? recommendedCta.fields.href : href,
            link_text: useRecommendedCta? recommendedCta.fields.link_text : module.fields.link_text,
            secondaryText: useRecommendedCta? recommendedCta.fields.secondaryText : module.fields.secondaryText,
            aria_label: useRecommendedCta? recommendedCta.fields.aria_label : module.fields.aria_label,
            link_type: module.fields.link_type,
            ctaColor: ctaColor,
            media: media,
            link_target: target,
            type: module.fields.link_target,
            link_caption: module.fields.link_caption,
            inheritParams: shouldInherit,
            phoneNumber: sitePhoneNumber,
            qr_code: module.fields.qr_code,
            theme: themeSwitch()(module.fields.link_type as ThemeType),
            size: module.fields.size ? module.fields.size : 'Large',
            colorSet: colorSetForTheme as SiteLinkColorSet,
            analytics_tags: module.fields.analytics_tags,
            offer_link: module.fields.offer_link,
        },
    };
}
